import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaArrowRight,
  FaCalendarAlt,
  FaMapMarkerAlt,
  FaTruck,
  FaReply,
  FaCog,
  FaUser,
  FaPlane,
  FaFileAlt,
  FaSpinner,
  FaListUl,
  FaBriefcase,
} from 'react-icons/fa';
import WebsiteTopBar from '../../components/WebsiteTopBar';
import HubHomeFooter from '../../components/hub/HubHomeFooter';
import { LOGISTICS_RFQ_ROUTES } from '../../constants/logisticsRfqRoutes';
import { useLocationPicker } from '../../hooks/useLocationPicker';
import { LOGISTICS_TRANSPORT_MODES } from '../../constants/logisticsProfileFormOptions';
import { useCurrencyMaster } from '../../hooks/useCurrencyMaster';
import { isValidPositiveDecimal } from './businessRelocationRfqFormState';
import {
  EL_PACKAGING_OPTIONS,
  EL_URGENCY_OPTIONS,
  EL_WEIGHT_UNIT_OPTIONS,
  EL_DIMENSION_UNIT_OPTIONS,
  hasPartialExhibitionDimensions,
  generateElRfqReference,
  getInitialExhibitionLogisticsRfqForm,
} from './exhibitionLogisticsRfqFormState';
import { VALUE_CURRENCY_OPTIONS } from './logisticsGenericRfqFormState';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/website-top-bar.scss';
import '../../assets/styles/exhibition_logistics_rfq.scss';
import {
  fetchBuyerRefCompanyFieldsForRfqPrefill,
  fetchBuyerCurrentLocationPrefillForBusinessRelocation,
  mergeBuyerPrefillIntoForm,
  mergeExhibitionOrgLocationPrefill,
} from './logisticsRfqBuyerPrefill';
import {
  checkExhibitionLogisticsDuplicate,
  submitExhibitionLogisticsRfq,
} from '../../services/exhibitionLogisticsRfq.service';
import SuperAdminStorageService from '../../services/SuperAdminStorageService';
import { getLogisticsRfqPostSubmitPath } from '../../utils/logisticsRfqPostSubmitNavigate';

function createClientRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `el-${Date.now()}-${Math.random().toString(36).slice(2, 12)}`;
}

function formatDuplicateStatus(status) {
  return String(status || '')
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatDuplicateSubmittedDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getReviewExistingExhibitionRfqPath(existingRfq) {
  if (SuperAdminStorageService.isAuthenticated()) {
    const id = existingRfq?.id;
    if (id != null && String(id).trim() !== '') {
      return `/super-admin/logistics/exhibition-logistics-rfqs?viewId=${encodeURIComponent(String(id))}`;
    }
    return '/super-admin/logistics/exhibition-logistics-rfqs';
  }
  const id = existingRfq?.id;
  if (id != null && String(id).trim() !== '') {
    return `/logistics/my-rfqs?exhibitionLogisticsRfqId=${encodeURIComponent(String(id))}`;
  }
  return '/logistics/my-rfqs';
}

/** e.g. `9-` or `9` from query → `9` */
function parseCompanyIdFromSearchParam(raw) {
  if (raw == null || String(raw).trim() === '') return null;
  const m = String(raw).trim().match(/^(\d+)/);
  const n = m ? parseInt(m[1], 10) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function Field({ label, required, children, className = '' }) {
  return (
    <div className={`elrq-field ${className}`.trim()}>
      <label className="elrq-label">
        {label}
        {required ? <span className="elrq-required">*</span> : null}
      </label>
      {children}
    </div>
  );
}

const TEXTAREA_MAX = 500;
const MATERIALS_TEXTAREA_MAX = 400;

function TextareaField({ label, required, value, onChange, placeholder, rows = 4, maxLen = TEXTAREA_MAX }) {
  const len = typeof value === 'string' ? value.length : 0;
  return (
    <Field label={label} required={required}>
      <div className="elrq-textarea-wrap">
        <textarea
          className="elrq-textarea"
          rows={rows}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
        />
        <span className="elrq-char-count" aria-hidden>
          {len}/{maxLen}
        </span>
      </div>
    </Field>
  );
}

function Section({ icon, title, subtitle, tone, children }) {
  return (
    <section className={`elrq-block elrq-block--${tone}`}>
      <header className="elrq-block-head">
        <span className="elrq-block-icon" aria-hidden>
          {icon}
        </span>
        <div>
          <h2 className="elrq-block-title">{title}</h2>
          {subtitle ? <p className="elrq-block-subtitle">{subtitle}</p> : null}
        </div>
      </header>
      <div className="elrq-block-body">{children}</div>
    </section>
  );
}

function ElLocationFields({ labels, fieldKeys, loc, form, setForm, addressRequired, addressPlaceholder, pincodePlaceholder }) {
  const ck = fieldKeys.country;
  const sk = fieldKeys.state;
  const cyk = fieldKeys.city;
  const pk = fieldKeys.pincode;
  const ak = fieldKeys.address;

  return (
    <>
      <div className="elrq-row elrq-row--4">
        <Field label={labels.country} required>
          <div className="elrq-select-wrap">
            <select
              className="elrq-select"
              value={form[ck] != null && form[ck] !== '' ? String(form[ck]) : ''}
              disabled={loc.loading}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({ ...prev, [ck]: v, [sk]: '', [cyk]: '' }));
                void loc.loadStates(v);
              }}
            >
              <option value="">{loc.loading ? 'Loading…' : 'Select country'}</option>
              {loc.countries.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.state} required>
          <div className="elrq-select-wrap">
            <select
              className="elrq-select"
              value={form[sk] != null && form[sk] !== '' ? String(form[sk]) : ''}
              disabled={!form[ck] || loc.loadingStates}
              onChange={(e) => {
                const v = e.target.value;
                setForm((prev) => ({ ...prev, [sk]: v, [cyk]: '' }));
                void loc.loadCities(v);
              }}
            >
              <option value="">{loc.loadingStates ? 'Loading…' : 'Select state'}</option>
              {loc.states.map((s) => (
                <option key={String(s.id)} value={String(s.id)}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.city} required>
          <div className="elrq-select-wrap">
            <select
              className="elrq-select"
              value={form[cyk] != null && form[cyk] !== '' ? String(form[cyk]) : ''}
              disabled={!form[sk] || loc.loadingCities}
              onChange={(e) => setForm((prev) => ({ ...prev, [cyk]: e.target.value }))}
            >
              <option value="">{loc.loadingCities ? 'Loading…' : 'Select city'}</option>
              {loc.cities.map((c) => (
                <option key={String(c.id)} value={String(c.id)}>
                  {c.label}
                </option>
              ))}
            </select>
          </div>
        </Field>
        <Field label={labels.pincode} required>
          <input
            className="elrq-input"
            value={form[pk]}
            onChange={(e) => setForm((prev) => ({ ...prev, [pk]: e.target.value }))}
            placeholder={pincodePlaceholder || 'e.g., 400001'}
          />
        </Field>
      </div>
      <Field label={labels.address} required={addressRequired}>
        <div className="elrq-textarea-wrap">
          <textarea
            className="elrq-textarea"
            rows={4}
            value={form[ak]}
            onChange={(e) => setForm((prev) => ({ ...prev, [ak]: e.target.value }))}
            placeholder={addressPlaceholder || ''}
          />
          <span className="elrq-char-count" aria-hidden>
            {typeof form[ak] === 'string' ? form[ak].length : 0}/{TEXTAREA_MAX}
          </span>
        </div>
      </Field>
    </>
  );
}

export default function ExhibitionLogisticsRfqPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const companyIdParam = useMemo(
    () => parseCompanyIdFromSearchParam(searchParams.get('companyId')),
    [searchParams],
  );

  const [form, setForm] = useState(() => getInitialExhibitionLogisticsRfqForm(companyIdParam));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [duplicateModal, setDuplicateModal] = useState(null);
  const submitInFlightRef = useRef(false);
  const clientRequestIdRef = useRef(null);

  useEffect(() => {
    setForm((prev) => ({ ...prev, companyId: companyIdParam }));
  }, [companyIdParam]);

  useEffect(() => {
    setForm((prev) => {
      if (prev.rfqReferenceNumber) return prev;
      return { ...prev, rfqReferenceNumber: generateElRfqReference() };
    });
  }, []);

  /** Same as generic RFQ: logged-in buyer contact / company from GET /company/my-profile (fills blanks only). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const mapped = await fetchBuyerRefCompanyFieldsForRfqPrefill();
      if (!cancelled) {
        setForm((prev) => mergeBuyerPrefillIntoForm(prev, mapped, 'exhibition-logistics'));
      }
      const locationPatch = await fetchBuyerCurrentLocationPrefillForBusinessRelocation();
      if (!cancelled && locationPatch) {
        setForm((prev) => mergeExhibitionOrgLocationPrefill(prev, locationPatch));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [companyIdParam]);

  const orgLoc = useLocationPicker();
  const venueLoc = useLocationPicker();
  const { currencyOptions } = useCurrencyMaster();

  const declaredValueCurrencyOptions = useMemo(() => {
    if (Array.isArray(currencyOptions) && currencyOptions.length > 0) {
      const seen = new Set();
      return currencyOptions
        .map((opt) => {
          const raw =
            (opt.currencyCode != null && String(opt.currencyCode).trim()) ||
            (typeof opt.value === 'string' && String(opt.value).trim()) ||
            '';
          const code = /^[A-Za-z]{2,4}$/.test(raw) ? raw.toUpperCase() : raw;
          return { value: code, label: code };
        })
        .filter((o) => o.value && !seen.has(o.value) && seen.add(o.value));
    }
    return VALUE_CURRENCY_OPTIONS.map((c) => ({ value: c, label: c }));
  }, [currencyOptions]);

  useEffect(() => {
    if (form.orgCountry) {
      void orgLoc.loadStates(String(form.orgCountry));
    } else {
      void orgLoc.loadStates('');
    }
  }, [form.orgCountry, orgLoc.loadStates]);

  useEffect(() => {
    if (form.orgState) {
      void orgLoc.loadCities(String(form.orgState));
    } else {
      void orgLoc.loadCities('');
    }
  }, [form.orgState, orgLoc.loadCities]);

  useEffect(() => {
    if (form.venueCountry) {
      void venueLoc.loadStates(String(form.venueCountry));
    } else {
      void venueLoc.loadStates('');
    }
  }, [form.venueCountry, venueLoc.loadStates]);

  useEffect(() => {
    if (form.venueState) {
      void venueLoc.loadCities(String(form.venueState));
    } else {
      void venueLoc.loadCities('');
    }
  }, [form.venueState, venueLoc.loadCities]);

  const setVal = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const toggleService = useCallback((key) => {
    setForm((prev) => ({
      ...prev,
      additionalServices: {
        ...prev.additionalServices,
        [key]: !prev.additionalServices[key],
      },
    }));
  }, []);

  const validateForm = useCallback(() => {
    if (!form.contactPerson?.trim()) {
      toast.error('Please enter contact person.');
      return false;
    }
    if (!form.contactNumber?.trim()) {
      toast.error('Please enter contact number.');
      return false;
    }
    if (!form.companyName?.trim()) {
      toast.error('Please enter company name.');
      return false;
    }
    if (!form.companyEmail?.trim()) {
      toast.error('Please enter company email.');
      return false;
    }
    if (!form.exhibitionName?.trim() || !form.setupDate?.trim()) {
      toast.error('Please enter exhibition name and setup date.');
      return false;
    }
    if (!form.orgCountry?.trim() || !form.orgState?.trim() || !form.orgCity?.trim() || !form.orgPincode?.trim()) {
      toast.error('Please complete organization country, state, city, and pincode.');
      return false;
    }
    if (
      !form.venueCountry?.trim() ||
      !form.venueState?.trim() ||
      !form.venueCity?.trim() ||
      !form.venuePincode?.trim() ||
      !form.venueAddress?.trim()
    ) {
      toast.error('Please complete exhibition venue location and venue address.');
      return false;
    }
    if (!form.materialsDescription?.trim() || !form.totalWeightKg?.toString().trim()) {
      toast.error('Please enter materials description and total weight.');
      return false;
    }
    if (!isValidPositiveDecimal(form.totalWeightKg)) {
      toast.error('Total weight must be a valid number greater than zero.');
      return false;
    }
    if (hasPartialExhibitionDimensions(form)) {
      toast.error('Please enter length, width, and height when providing dimensions.');
      return false;
    }
    if (!form.logisticsMode) {
      toast.error('Please select preferred logistics mode.');
      return false;
    }
    return true;
  }, [form]);

  const performSubmit = useCallback(
    async ({ confirmDuplicateSubmit = false } = {}) => {
      if (submitInFlightRef.current) return;
      submitInFlightRef.current = true;
      setIsSubmitting(true);

      if (confirmDuplicateSubmit) {
        clientRequestIdRef.current = createClientRequestId();
      } else if (!clientRequestIdRef.current) {
        clientRequestIdRef.current = createClientRequestId();
      }

      const payload = {
        ...form,
        companyId: companyIdParam ?? form.companyId ?? null,
        ...(confirmDuplicateSubmit ? { rfqReferenceNumber: generateElRfqReference() } : {}),
      };

      try {
        const res = await submitExhibitionLogisticsRfq(payload, {
          confirmDuplicateSubmit,
          clientRequestId: clientRequestIdRef.current,
        });

        if (res?.success) {
          toast.success(
            res?.message || 'Exhibition Logistics RFQ submitted successfully.',
          );
          clientRequestIdRef.current = null;
          setDuplicateModal(null);
          navigate(getLogisticsRfqPostSubmitPath('exhibition-logistics'), { replace: true });
        } else {
          toast.error(res?.message || 'Submission failed.');
        }
      } catch (err) {
        if (err?.response?.status === 409) {
          const m = err?.response?.data?.message || 'You have already submitted this RFQ.';
          toast.error(typeof m === 'string' ? m : 'You have already submitted this RFQ.');
          return;
        }
        const msg =
          err?.response?.data?.message ||
          err?.response?.data?.error ||
          err?.message ||
          'Could not submit. Please try again.';
        toast.error(typeof msg === 'string' ? msg : 'Submission failed.');
      } finally {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    },
    [form, companyIdParam, navigate],
  );

  const submit = useCallback(async () => {
    if (!validateForm()) return;
    if (submitInFlightRef.current) return;

    setIsSubmitting(true);
    try {
      const dupData = await checkExhibitionLogisticsDuplicate(form);
      if (dupData?.hasDuplicate && dupData?.existingRfq) {
        setDuplicateModal(dupData.existingRfq);
        setIsSubmitting(false);
        return;
      }
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.message ||
        'Could not check for similar RFQs. Please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Could not continue.');
      setIsSubmitting(false);
      return;
    }

    await performSubmit({ confirmDuplicateSubmit: false });
  }, [form, validateForm, performSubmit]);

  const handleSubmitAnyway = useCallback(() => {
    void performSubmit({ confirmDuplicateSubmit: true });
  }, [performSubmit]);

  const handleReviewExistingRfq = useCallback(() => {
    if (!duplicateModal) return;
    const path = getReviewExistingExhibitionRfqPath(duplicateModal);
    setDuplicateModal(null);
    submitInFlightRef.current = false;
    setIsSubmitting(false);
    navigate(path);
  }, [duplicateModal, navigate]);

  const closeDuplicateModal = useCallback(() => {
    setDuplicateModal(null);
    submitInFlightRef.current = false;
    setIsSubmitting(false);
  }, []);

  const providerLabel =
    companyIdParam != null ? `Provider company ID: ${companyIdParam}` : 'No specific provider selected';

  return (
    <div className="elrq-page">
      <WebsiteTopBar />
      <main className="elrq-main">
        <div className="elrq-breadcrumb-row">
          <nav className="elrq-breadcrumb" aria-label="Breadcrumb">
            <Link to="/home">Home</Link>
            <span className="elrq-breadcrumb-sep">&gt;</span>
            <Link to={LOGISTICS_RFQ_ROUTES.selection}>RFQ</Link>
            <span className="elrq-breadcrumb-sep">&gt;</span>
            <span className="elrq-breadcrumb-current">Logistics</span>
          </nav>
          <Link to={LOGISTICS_RFQ_ROUTES.selection} className="elrq-back-link">
            ← Back
          </Link>
        </div>

        <div className="elrq-top">
          <div className="elrq-title-row">
            <span className="elrq-title-icon" aria-hidden>
              <FaPlane />
            </span>
            <div className="elrq-title-text">
              <h1 className="elrq-page-title">Exhibition Logistics Quote</h1>
              <p className="elrq-page-subtitle">
                Fill-in overall logistics solution for your exhibition participation.
              </p>
            </div>
          </div>
          {companyIdParam != null ? (
            <p className="elrq-provider-pill">{providerLabel}</p>
          ) : null}
        </div>

        <div className="elrq-card">
          <div className="elrq-card-inner">
            <Section icon={<FaUser />} title="Contact Information" tone="lavender">
              <div className="elrq-row elrq-row--4">
                <Field label="Contact Person" required>
                  <input
                    className="elrq-input"
                    value={form.contactPerson}
                    onChange={(e) => setVal('contactPerson', e.target.value)}
                    placeholder="Eg: John Doe"
                  />
                </Field>
                <Field label="Contact Number" required>
                  <input
                    className="elrq-input"
                    value={form.contactNumber}
                    onChange={(e) => setVal('contactNumber', e.target.value)}
                    placeholder="+91 88765 43210"
                  />
                </Field>
                <Field label="Company Name" required>
                  <input
                    className="elrq-input"
                    value={form.companyName}
                    onChange={(e) => setVal('companyName', e.target.value)}
                    placeholder="Enter Company Name"
                  />
                </Field>
                <Field label="Company Email" required>
                  <input
                    type="email"
                    className="elrq-input"
                    value={form.companyEmail}
                    onChange={(e) => setVal('companyEmail', e.target.value)}
                    placeholder="company@example.com"
                  />
                </Field>
              </div>
            </Section>

            <Section icon={<FaFileAlt />} title="Exhibition Details" tone="blue">
              <div className="elrq-row elrq-row--2">
                <div className="elrq-field elrq-field--block">
                  <label className="elrq-label">
                    Exhibition/Expo Name<span className="elrq-required">*</span>
                  </label>
                  <input
                    className="elrq-input"
                    value={form.exhibitionName}
                    onChange={(e) => setVal('exhibitionName', e.target.value)}
                    placeholder="Eg: Global Expo 2026"
                  />
                </div>
                <Field label="Expo Code">
                  <input
                    className="elrq-input"
                    value={form.expoCode}
                    onChange={(e) => setVal('expoCode', e.target.value)}
                    placeholder="Optional exhibitor / expo code"
                  />
                </Field>
              </div>
              <div className="elrq-row elrq-row--3">
                <Field label="Setup Date" required>
                  <div className="elrq-input-wrap elrq-input-wrap--date">
                    <input
                      type="date"
                      className="elrq-input elrq-input--date"
                      value={form.setupDate}
                      onChange={(e) => setVal('setupDate', e.target.value)}
                    />
                    <span className="elrq-input-suffix-icon" aria-hidden>
                      <FaCalendarAlt />
                    </span>
                  </div>
                </Field>
                <Field label="Exhibition Start Date">
                  <div className="elrq-input-wrap elrq-input-wrap--date">
                    <input
                      type="date"
                      className="elrq-input elrq-input--date"
                      value={form.exhibitionStartDate}
                      onChange={(e) => setVal('exhibitionStartDate', e.target.value)}
                    />
                    <span className="elrq-input-suffix-icon" aria-hidden>
                      <FaCalendarAlt />
                    </span>
                  </div>
                </Field>
                <Field label="Exhibition End Date">
                  <div className="elrq-input-wrap elrq-input-wrap--date">
                    <input
                      type="date"
                      className="elrq-input elrq-input--date"
                      value={form.exhibitionEndDate}
                      onChange={(e) => setVal('exhibitionEndDate', e.target.value)}
                    />
                    <span className="elrq-input-suffix-icon" aria-hidden>
                      <FaCalendarAlt />
                    </span>
                  </div>
                </Field>
              </div>
            </Section>

            <div className="elrq-location-pair">
              <Section icon={<FaListUl />} title="Organization Location Details" tone="green">
                <ElLocationFields
                  labels={{
                    country: 'Organization Country',
                    state: 'Organization State/Province',
                    city: 'Organization City',
                    pincode: 'Organization Pincode/ZIP',
                    address: 'Organization Address',
                  }}
                  fieldKeys={{
                    country: 'orgCountry',
                    state: 'orgState',
                    city: 'orgCity',
                    pincode: 'orgPincode',
                    address: 'orgAddress',
                  }}
                  loc={orgLoc}
                  form={form}
                  setForm={setForm}
                  addressRequired={false}
                  addressPlaceholder="Enter complete address with landmark"
                />
              </Section>

              <Section icon={<FaMapMarkerAlt />} title="Exhibition Venue Location" tone="sky">
                <ElLocationFields
                  labels={{
                    country: 'Exhibition Country',
                    state: 'Exhibition State/Province',
                    city: 'Exhibition City',
                    pincode: 'Exhibition Pincode/ZIP',
                    address: 'Complete Venue Address',
                  }}
                  fieldKeys={{
                    country: 'venueCountry',
                    state: 'venueState',
                    city: 'venueCity',
                    pincode: 'venuePincode',
                    address: 'venueAddress',
                  }}
                  loc={venueLoc}
                  form={form}
                  setForm={setForm}
                  addressRequired
                  addressPlaceholder="Provide the exact venue address where exhibit will be set up"
                />
                <Field label="Booth Number">
                  <input
                    className="elrq-input"
                    value={form.boothNumber}
                    onChange={(e) => setVal('boothNumber', e.target.value)}
                    placeholder="Eg: Hall 5, Booth A-12"
                  />
                </Field>
              </Section>
            </div>

            <Section icon={<FaBriefcase />} title="Cargo & Materials Details" tone="orange">
              <TextareaField
                label="Materials Description"
                required
                maxLen={MATERIALS_TEXTAREA_MAX}
                value={form.materialsDescription}
                onChange={(e) => setVal('materialsDescription', e.target.value)}
                placeholder="Describe the type and number of materials."
              />
              <div className="elrq-row elrq-row--4">
                <Field label="Total Weight" required>
                  <div className="elrq-input-group">
                    <input
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      className="elrq-input-group__control"
                      value={form.totalWeightKg}
                      onChange={(e) => setVal('totalWeightKg', e.target.value)}
                      placeholder="e.g., 500"
                      aria-label="Total weight value"
                    />
                    <div className="elrq-input-group__addon elrq-input-group__addon--compact">
                      <select
                        className="elrq-input-group__select elrq-input-group__select--compact"
                        value={form.totalWeightUnit || 'KG'}
                        onChange={(e) => setVal('totalWeightUnit', e.target.value)}
                        aria-label="Total weight unit"
                      >
                        {EL_WEIGHT_UNIT_OPTIONS.map((unit) => (
                          <option key={unit} value={unit}>
                            {unit}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </Field>
                <Field label="Declared Value">
                  <div className="elrq-input-group elrq-input-group--prefix">
                    <div className="elrq-input-group__addon elrq-input-group__addon--compact">
                      <select
                        className="elrq-input-group__select elrq-input-group__select--compact elrq-input-group__select--currency"
                        value={form.declaredValueCurrency}
                        onChange={(e) => setVal('declaredValueCurrency', e.target.value)}
                        aria-label="Declared value currency"
                      >
                        {declaredValueCurrencyOptions.map((c) => (
                          <option key={c.value} value={c.value}>
                            {c.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <input
                      type="number"
                      min={0}
                      step="any"
                      inputMode="decimal"
                      className="elrq-input-group__control"
                      value={form.declaredValueUsd}
                      onChange={(e) => setVal('declaredValueUsd', e.target.value)}
                      placeholder="e.g., 50000"
                      aria-label="Declared value amount"
                    />
                  </div>
                </Field>
                <Field label="Number of Packages">
                  <input
                    type="number"
                    min={0}
                    step={1}
                    className="elrq-input"
                    value={form.numberOfPackages}
                    onChange={(e) => setVal('numberOfPackages', e.target.value)}
                    placeholder="e.g., 12"
                  />
                </Field>
                <Field label="Packaging Type">
                  <div className="elrq-select-wrap">
                    <select
                      className="elrq-select"
                      value={form.packagingType}
                      onChange={(e) => setVal('packagingType', e.target.value)}
                    >
                      {EL_PACKAGING_OPTIONS.map((t) => (
                        <option key={t || 'x'} value={t}>
                          {t || 'Select package'}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
              <div className="elrq-row elrq-row--4">
                <Field label="Length">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    className="elrq-input"
                    value={form.dimensionLength}
                    onChange={(e) => setVal('dimensionLength', e.target.value)}
                    placeholder="e.g., 120"
                  />
                </Field>
                <Field label="Width">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    className="elrq-input"
                    value={form.dimensionWidth}
                    onChange={(e) => setVal('dimensionWidth', e.target.value)}
                    placeholder="e.g., 80"
                  />
                </Field>
                <Field label="Height">
                  <input
                    type="number"
                    min={0}
                    step="any"
                    inputMode="decimal"
                    className="elrq-input"
                    value={form.dimensionHeight}
                    onChange={(e) => setVal('dimensionHeight', e.target.value)}
                    placeholder="e.g., 60"
                  />
                </Field>
                <Field label="Unit">
                  <div className="elrq-select-wrap">
                    <select
                      className="elrq-select"
                      value={form.dimensionUnit || 'CM'}
                      onChange={(e) => setVal('dimensionUnit', e.target.value)}
                      aria-label="Dimension unit"
                    >
                      {EL_DIMENSION_UNIT_OPTIONS.map((unit) => (
                        <option key={unit} value={unit}>
                          {unit}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
              </div>
            </Section>

            <Section icon={<FaTruck />} title="Logistics Preferences" tone="purple">
              <div className="elrq-row elrq-row--3">
                <Field label="Preferred Logistics Mode" required>
                  <div className="elrq-select-wrap">
                    <select
                      className="elrq-select"
                      value={form.logisticsMode}
                      onChange={(e) => setVal('logisticsMode', e.target.value)}
                    >
                      <option value="">Select Mode</option>
                      {LOGISTICS_TRANSPORT_MODES.map((m) => (
                        <option key={m.id} value={m.id}>
                          {m.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
                <Field label="Shipment Type">
                  <div className="elrq-select-wrap">
                    <select
                      className="elrq-select"
                      value={form.urgencyLevel}
                      onChange={(e) => setVal('urgencyLevel', e.target.value)}
                    >
                      {EL_URGENCY_OPTIONS.map((t) => (
                        <option key={t || 'x'} value={t}>
                          {t || 'Select option'}
                        </option>
                      ))}
                    </select>
                  </div>
                </Field>
                <Field label="Special Handling Requirements">
                  <input
                    className="elrq-input"
                    value={form.specialHandling}
                    onChange={(e) => setVal('specialHandling', e.target.value)}
                    placeholder="Any special handling requirements"
                  />
                </Field>
              </div>
            </Section>

            <Section icon={<FaReply />} title="Return Logistics" tone="rose">
              <label className="elrq-check elrq-check--solo">
                <input
                  type="checkbox"
                  checked={form.returnToOrigin}
                  onChange={(e) => setVal('returnToOrigin', e.target.checked)}
                />
                Enable return logistics once the exhibition is completed.
              </label>
            </Section>

            <Section icon={<FaCog />} title="Additional Services" tone="slate">
              <div className="elrq-svc-grid">
                <label className="elrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.cargoInsurance}
                    onChange={() => toggleService('cargoInsurance')}
                  />
                  Cargo Insurance Coverage
                </label>
                <label className="elrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.labourSupport}
                    onChange={() => toggleService('labourSupport')}
                  />
                  Special Handling / Equipment
                </label>
                <label className="elrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.boothSetupDismantling}
                    onChange={() => toggleService('boothSetupDismantling')}
                  />
                  On-site Setup &amp; Dismantling
                </label>
                <label className="elrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.customsClearance}
                    onChange={() => toggleService('customsClearance')}
                  />
                  Customs Clearance Support
                </label>
                <label className="elrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.tempStorageVenue}
                    onChange={() => toggleService('tempStorageVenue')}
                  />
                  Temporary Storage at Venue
                </label>
                <label className="elrq-check">
                  <input
                    type="checkbox"
                    checked={form.additionalServices.realTimeTracking}
                    onChange={() => toggleService('realTimeTracking')}
                  />
                  Live Tracking &amp; Updates
                </label>
              </div>
            </Section>
          </div>

          <footer className="elrq-card-footer">
            <Link
              to="/home"
              className="elrq-btn elrq-btn--outline"
              aria-disabled={isSubmitting}
              onClick={(e) => {
                if (isSubmitting) e.preventDefault();
              }}
            >
              Back
            </Link>
            <button
              type="button"
              className="elrq-btn elrq-btn--primary"
              onClick={submit}
              disabled={isSubmitting || Boolean(duplicateModal)}
            >
              {isSubmitting ? (
                <>
                  <FaSpinner className="elrq-btn-spinner" aria-hidden />
                  Submitting…
                </>
              ) : (
                <>
                  Get Exhibition Quote
                  <FaArrowRight className="elrq-btn-arrow" aria-hidden />
                </>
              )}
            </button>
          </footer>
        </div>
      </main>
      <HubHomeFooter />
      {duplicateModal && typeof document !== 'undefined'
        ? createPortal(
            <div className="elrq-dup-overlay" role="presentation" onClick={closeDuplicateModal}>
              <div
                className="elrq-dup-modal"
                role="dialog"
                aria-modal="true"
                aria-labelledby="elrq-dup-title"
                onClick={(e) => e.stopPropagation()}
              >
                <h2 id="elrq-dup-title" className="elrq-dup-title">
                  A similar Exhibition Logistics RFQ already exists.
                </h2>
                <p className="elrq-dup-lead">Do you want to continue?</p>
                <dl className="elrq-dup-meta">
                  <div>
                    <dt>RFQ Number</dt>
                    <dd>{duplicateModal.rfqNumber || duplicateModal.rfqReference || '—'}</dd>
                  </div>
                  <div>
                    <dt>Submitted On</dt>
                    <dd>{formatDuplicateSubmittedDate(duplicateModal.submittedDate)}</dd>
                  </div>
                  <div>
                    <dt>Current Status</dt>
                    <dd>{formatDuplicateStatus(duplicateModal.status)}</dd>
                  </div>
                </dl>
                <div className="elrq-dup-actions">
                  <button
                    type="button"
                    className="elrq-btn elrq-btn--outline"
                    onClick={handleReviewExistingRfq}
                    disabled={isSubmitting}
                  >
                    Review Existing RFQ
                  </button>
                  <button
                    type="button"
                    className="elrq-btn elrq-btn--primary"
                    onClick={handleSubmitAnyway}
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <FaSpinner className="elrq-btn-spinner" aria-hidden />
                        Submitting…
                      </>
                    ) : (
                      'Submit Anyway'
                    )}
                  </button>
                  <button
                    type="button"
                    className="elrq-btn elrq-btn--outline"
                    onClick={closeDuplicateModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
